import { useEffect, useMemo } from 'react';
import isHotkey from 'is-hotkey';

import {
  AnatomicNavigatorPlugin,
  BoldPlugin,
  ParagraphPlugin,
  ItalicPlugin,
  ListPlugin,
  UnderlinePlugin,
  PicklistPlugin,
  PlaceholderPlugin,
  InlineBookmarkPlugin,
  ClearFormattingPlugin,
  DictaphoneControlsPlugin,
  GeneralKeyboardControlsPlugin,
  DictationPlugin,
  ExpandSelectionPlugin,
  DeepLinkPlugin,
  HighlightedPlugin,
  InsertionMarkPlugin,
  HeadingDecorationPlugin,
  buildHeadingLabelSet,
  MacroPlaceholderPlugin,
  MacroPlaceholderRenderMode,
  HeadingPlugin,
  AutoCorrectPlugin,
  TextSourceStylingPlugin,
} from '../../RichTextEditor';
import type { AllReporterVariant, EditorPlugin } from '../../RichTextEditor';
import { DISCRIMINATORY_WORDS } from 'config/constants';
import { useToolPreferences } from 'hooks/usePreferences';
import { generateGlobalReporterShortcutsHandlers } from 'hooks/useCrossWindowReporterShortcuts';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { usePicklistStore } from '../../Template/usePicklist';
import { useHeadingKeywords } from 'hooks/useHeadingKeywords';
import { HeadingErrorPlugin } from '../../RichTextEditor/plugins/headingError/headingErrorPlugin';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { setHeadingLikeFieldNamingEnabled } from '../../RichTextEditor/utils/fieldNaming';
import { useCurrentListStore } from '../../RichTextEditor/plugins/list/hooks/useCurrentList';
import { handlePlaceholderItemClick } from '../../RichTextEditor/plugins/placeholder/utils';
import { RequiredFieldsPlugin } from '../../RichTextEditor/plugins/requiredFields/requiredFieldsPlugin';
import { useToasterDispatch } from 'common/ui/Toaster/Toaster';
import { useRequiredFieldIndicatorHandlers } from 'hooks/useRequiredFieldIndicator';
import { useFocusMode } from 'hooks/useFocusMode';
import { useDeepLinkStudyHandlers } from 'hooks/useDeepLinkStudy';
import { useRecoilValue } from 'recoil';
import { isInsertingMacroState } from 'domains/reporter/Reporter/hooks/useInsertMacro/useInsertMacro';
import type { WithApolloContext } from 'modules/Apollo/types';

type UseReporterEditorPluginsProps = Readonly<{
  variant?: AllReporterVariant;
  getActiveTemplateId?: () => string | undefined;
  context: WithApolloContext['context'];
}>;

export const useReporterEditorPlugins = ({
  variant = 'report',
  getActiveTemplateId,
  context,
}: UseReporterEditorPluginsProps): EditorPlugin[] => {
  const { data } = useCurrentUser({ context });
  const headingKeywords = useHeadingKeywords({ context });
  const isInsertingMacro = useRecoilValue(isInsertingMacroState);
  const { enqueueToast } = useToasterDispatch();
  const reporterSettings = data?.me?.reporterSettings;
  const nvoqCredentials = data?.me?.nvoqCredentials;
  const [toolPreferences] = useToolPreferences({ context });
  const ignoreMergeFieldsInNavigation =
    reporterSettings?.mergeFieldsSettings?.ignoreNavigation ?? false;
  const { copyPicklist, cacheClipboardPicklists } = usePicklistStore();
  const { listToolbarModes, setCurrentActiveListVariant } = useCurrentListStore();
  const [isReporterAnatomicNavigatorEnabled] = useFeatureFlagEnabled(
    FF.REPORTER_ANATOMIC_NAVIGATOR
  );
  const [isMacroPlaceholderChipEnabled] = useFeatureFlagEnabled(FF.REPORTER_MACRO_PLACEHOLDER_CHIP);
  // RP-6005 (M2) — gate headings-as-decoration on the rollout flag (M0).
  const [isHeadingNodesDeprecated] = useFeatureFlagEnabled(FF.REPORTER_HEADING_NODES_DEPRECATED);
  // RP-6006 (M3) — mirror the flag into fieldNaming so its heading-like-paragraph
  // fallback (which runs in normalize/stitching, without hook access) stays inert
  // while the flag is off.
  useEffect(() => {
    setHeadingLikeFieldNamingEnabled(isHeadingNodesDeprecated);
    // Reset the module-level toggle on unmount so it can't leak into later `getDefaultFieldName`
    // calls elsewhere in the session (e.g. story transitions or other editor surfaces).
    return () => setHeadingLikeFieldNamingEnabled(false);
  }, [isHeadingNodesDeprecated]);
  const { syncRequiredFields, onInsertTextRequiredField } = useRequiredFieldIndicatorHandlers();
  const isAutoFillComparisonEnabled = reporterSettings?.autoFillComparison?.enabled;
  const { toggleFocusMode } = useFocusMode({ context });
  const {
    handleDeepLinkClick,
    handleEditDeepLinkClick,
    handleStudyDeepLinkRemove,
    handleStudyDeepLinkRestore,
    getIsEditingStudyNode,
  } = useDeepLinkStudyHandlers({
    isAutoFillComparisonEnabled,
    context,
  });

  return useMemo(() => {
    if (
      nvoqCredentials == null ||
      nvoqCredentials.username == null ||
      nvoqCredentials.password == null
    ) {
      return [];
    }

    return [
      ExpandSelectionPlugin(),
      DictationPlugin({
        nvoqID: nvoqCredentials?.username,
        nvoqAuthorization: nvoqCredentials?.password,
        getActiveTemplateId,
        target: 'reporter',
        enablePicklistDictation: true,
      }),
      HighlightedPlugin({
        words: DISCRIMINATORY_WORDS,
      }),
      ...(isHeadingNodesDeprecated
        ? [HeadingDecorationPlugin({ headingLabels: buildHeadingLabelSet(headingKeywords) })]
        : []),
      AutoCorrectPlugin(),
      ...(isReporterAnatomicNavigatorEnabled ? [AnatomicNavigatorPlugin()] : []),
      HeadingPlugin({
        headingKeywords,
        isInsertingMacro,
        // RP-6010 (M7): no H1/H2 toolbar buttons when headings are decorated paragraphs.
        showHeadingToolbarActions: !isHeadingNodesDeprecated,
      }),
      BoldPlugin(),
      DeepLinkPlugin({
        ignoreMergeFieldsInNavigation,
        isAutoFillComparisonEnabled,
        onDeepLinkClick: handleDeepLinkClick,
        onDeepLinkEditClick: handleEditDeepLinkClick,
        onStudyDeepLinkRemove: isAutoFillComparisonEnabled ? handleStudyDeepLinkRemove : undefined,
        onStudyDeepLinkRestore: isAutoFillComparisonEnabled
          ? handleStudyDeepLinkRestore
          : undefined,
        getIsEditingStudyNode,
      }),
      ParagraphPlugin(),
      ItalicPlugin(),
      UnderlinePlugin(),
      TextSourceStylingPlugin(),
      ClearFormattingPlugin(),
      ListPlugin({
        toolbarMode: listToolbarModes.reporter,
        setCurrentActiveListVariant,
      }),
      // Required fields must come before picklist and inline bookmark plugins
      RequiredFieldsPlugin({
        enqueueToast,
        variant,
        onInsertTextRequiredField,
        syncRequiredFields,
      }),
      // Picklist must come before all other inline template plugins
      PicklistPlugin({
        showPicklistOptionsInEditor: toolPreferences?.dictaphone.showPicklistOptionsInEditor,
        onPicklistCopy: copyPicklist,
        onPicklistFragmentCopy: cacheClipboardPicklists,
      }),
      InlineBookmarkPlugin(),
      PlaceholderPlugin({
        onPlaceholderMenuClick: handlePlaceholderItemClick,
      }),
      // Always last so other plugins can handle the button presses
      DictaphoneControlsPlugin({
        ignoreMergeFieldsInNavigation,
        toggleFocusMode,
      }),
      GeneralKeyboardControlsPlugin({
        ignoreMergeFieldsInNavigation,
        onKeyDownOverride: (e) => {
          if (!toolPreferences) return false;

          return generateGlobalReporterShortcutsHandlers(toolPreferences.keyboard.shortcuts).some(
            (handler) => {
              if (isHotkey(handler.key, e)) {
                e.preventDefault();

                handler.callback(new Event('keydown'));
                return true;
              }

              return false;
            }
          );
        },
      }),
      // Plugin is only injected to support ad-hoc macros since they create synthetic macroPlaceholders
      // to be able to stitch in a macro at a given path.
      //
      // CANNOT BE USED AS A NODE IN A REPORT
      MacroPlaceholderPlugin({
        renderMode: isMacroPlaceholderChipEnabled
          ? MacroPlaceholderRenderMode.ChipElement
          : MacroPlaceholderRenderMode.TemplateElement,
      }),
      HeadingErrorPlugin(),
      InsertionMarkPlugin(),
    ];
  }, [
    nvoqCredentials,
    getActiveTemplateId,
    isReporterAnatomicNavigatorEnabled,
    headingKeywords,
    isInsertingMacro,
    ignoreMergeFieldsInNavigation,
    isAutoFillComparisonEnabled,
    handleDeepLinkClick,
    handleEditDeepLinkClick,
    handleStudyDeepLinkRemove,
    handleStudyDeepLinkRestore,
    getIsEditingStudyNode,
    listToolbarModes.reporter,
    setCurrentActiveListVariant,
    enqueueToast,
    variant,
    onInsertTextRequiredField,
    syncRequiredFields,
    toolPreferences,
    copyPicklist,
    cacheClipboardPicklists,
    toggleFocusMode,
    isMacroPlaceholderChipEnabled,
    isHeadingNodesDeprecated,
  ]);
};
